import {BaseQueryFn, fetchBaseQuery, FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {addBreadcrumb, captureException} from '@sentry/browser';
import flagsmith from 'flagsmith';

// eslint-disable-next-line no-restricted-imports
import {isNotNil, not, omit, path} from 'ramda';

import {browserStorageKey, shouldDisableMandatoryFields} from '@dms/config';
import {getWorkspaceFromUri, redirectToLogin, redirectToWorkspace} from '@dms/routes';

import {
  BaseQueryExtraOptions,
  CI_MODE_ON,
  parseZodSchema,
  ParseZodSchemaQueryInfo,
} from 'shared/web';

import {publicApi} from '../modules/publicApi/publicApi';
import {RefreshTokenApiArg} from '../modules/publicApi/types';
import {isCI} from './constants/isCI';
import {isDev} from './constants/isDev';
import {initializeAxios} from './initializeAxios';
import {FetchBaseQueryArgs} from './types/FetchBaseQueryArgs';

const RETRY_DELAY = 3_000;
const INFRASTRUCTURE_ERRORS_CODES = [502, 503, 504];

const getArgsWithAuthorization = (args: FetchBaseQueryArgs, branch?: string) => {
  const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
  const isCiModeOn = localStorage.getItem(browserStorageKey.CI_MODE) === CI_MODE_ON;
  const {workspace} = getWorkspaceFromUri();

  const headers = {
    'accept-language': localStorage.getItem(browserStorageKey.LAST_KNOWN_LANGUAGE),
    'x-country-format': 'iso', // https://carvago.slack.com/archives/G01AN5XCXGX/p1634202539053900
    credentials: 'include',
    ...(accessToken ? {Authorization: `Bearer ${accessToken}`} : {}),
    ...(branch ? {'X-Branch': branch} : {}),
    ...(workspace ? {'X-Workspace': workspace} : {}),
    ...(isCiModeOn ? {'x-debug-mode': 'true'} : {}),
    ...args.headers,
  };

  return {...omit(['omitHeaders'], args), headers: omit(args.omitHeaders ?? [], headers)};
};

const fetchBaseQueryInstance = fetchBaseQuery({baseUrl: process.env.API_URL});

export const teasBaseQuery: BaseQueryFn<
  FetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  initializeAxios(api);
  const activeBranchId = path<string>(['branch', 'activeBranchId'], api.getState());

  // Override validateMandatoryFields if dev setting is enabled
  if (shouldDisableMandatoryFields() && isNotNil(args.params?.validateMandatoryFields)) {
    args = {
      ...args,
      params: {
        ...args.params,
        validateMandatoryFields: false,
      },
    };
  }

  const queryInfo: Omit<ParseZodSchemaQueryInfo, 'schemaType'> = {
    method: args.method,
    endpointName: api.endpoint,
    url: args.url,
  };

  if (isDev && not(isCI) && extraOptions && extraOptions.requestSchema) {
    parseZodSchema({
      data: args.params,
      schema: extraOptions.requestSchema,
      queryInfo: {...queryInfo, schemaType: 'request'},
    });
  }

  let result = await fetchBaseQueryInstance(
    getArgsWithAuthorization(args, activeBranchId),
    api,
    extraOptions
  );

  if (isDev && not(isCI) && extraOptions && extraOptions.responseSchema && 'data' in result) {
    parseZodSchema({
      data: result.data,
      schema: extraOptions.responseSchema,
      queryInfo: {...queryInfo, schemaType: 'response'},
    });
  }

  /**
   * This retry logic is used to handle infrastructure errors. This type of errors are often ad-hoc
   * and not related to the request payload, so retrying the request should help.
   * We also try to retry general 500 errors as this may also help.
   * https://carvago.atlassian.net/browse/T20-80058
   */
  if (result.error) {
    if (
      INFRASTRUCTURE_ERRORS_CODES.includes(
        Number(result.meta?.response?.status ?? result.error.status)
      )
    ) {
      addBreadcrumb({
        category: 'api',
        message: `Retrying PHP API request ${args.url}, because response status is ${result.meta?.response?.status ?? result.error.status}`,
        level: 'error',
      });
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      result = await fetchBaseQueryInstance(
        getArgsWithAuthorization(args, activeBranchId),
        api,
        extraOptions
      );
    }
  }

  if (result.error && Number(result.error.status) > 500) {
    captureException(result.error);
  }

  if (result.error && result.error.status === 401) {
    const getRefreshToken = localStorage.getItem(browserStorageKey.REFRESH_TOKEN);
    const {workspace, shouldRedirectToAppWorkspace} = getWorkspaceFromUri();
    const currentUserEmail = path<string>(['user', 'email'], api.getState());

    if (!workspace || shouldRedirectToAppWorkspace) {
      redirectToWorkspace();
      return result;
    }

    if (!getRefreshToken) {
      redirectToLogin({currentUserEmail});
      return result;
    }

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('directLink');

    const refreshTokenArgs: RefreshTokenApiArg = {
      workspace,
      refreshTokenRequestBody: {refreshToken: getRefreshToken},
    };

    await api
      .dispatch(publicApi.endpoints.refreshToken.initiate(refreshTokenArgs))
      .unwrap()
      .then(({refreshToken, token}) => {
        sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, token);
        localStorage.setItem(browserStorageKey.REFRESH_TOKEN, refreshToken);
      })
      .catch(() => {
        sessionStorage.removeItem(browserStorageKey.ACCESS_TOKEN);
        localStorage.removeItem(browserStorageKey.REFRESH_TOKEN);
        flagsmith.logout();
        redirectToLogin({currentUserEmail});
      });

    result = await fetchBaseQueryInstance(
      getArgsWithAuthorization(args, activeBranchId),
      api,
      extraOptions
    );
  }

  return result;
};
